import React, { useEffect, useState, useRef } from "react";
import Sidebar from "./Sidebar";
import Header from "./Header";
import DashboardFooter from "./DashboardFooter";
import "bootstrap/dist/css/bootstrap.min.css";
import "../css/FeeDetails.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import * as XLSX from "xlsx";
import {
  FaEdit,
  FaTrash,
  FaEye,
  FaDownload,
  FaPlus,
  FaSync,
  FaFilter,
  FaTimes,
  FaSearch,
  FaTable,
  FaColumns
} from "react-icons/fa";

const API_URL = "https://auxititpt.org/Backend/fees_api.php";
const LETTERHEAD_IMAGE =
  "https://auxititpt.org/public/uploads/Letter_Head_Axilium_English.jpg";

const FeeDetails = () => {
  const [students, setStudents] = useState([]);
  const [trades, setTrades] = useState([]);
  const [batches, setBatches] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editFee, setEditFee] = useState(null);
  const [viewFee, setViewFee] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  // Filters state
  const [filterTrades, setFilterTrades] = useState([]);
  const [filterBatches, setFilterBatches] = useState([]);
  const [filterFeeTypes, setFilterFeeTypes] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // Modal temp selections
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [tempTrades, setTempTrades] = useState([]);
  const [tempBatches, setTempBatches] = useState([]);
  const [tempFeeTypes, setTempFeeTypes] = useState([]);

  // Receipt form state
  const [selectedTrade, setSelectedTrade] = useState("");
  const [selectedBatch, setSelectedBatch] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [student, setStudent] = useState(null);
  const [receiptNo, setReceiptNo] = useState("");

  const [formData, setFormData] = useState({
    feeType: "",
    amount: "",
    paymentMode: "",
    purpose: "",
    date: new Date().toISOString().split("T")[0],
  });

  const printRef = useRef();

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchStudents(), fetchFees()]);
    setLoading(false);
  };

  const fetchStudents = async () => {
    try {
      const res = await fetch(`${API_URL}?type=students`);
      const data = await res.json();
      setStudents(data || []);

      const uniqueTrades = [...new Set((data || []).map((s) => s.trade))].sort();
      setTrades(uniqueTrades);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchFees = async () => {
    try {
      const res = await fetch(`${API_URL}?type=fees`);
      const data = await res.json();
      setFees(data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const generateReceiptNo = async () => {
    try {
      const response = await fetch(`${API_URL}?generate_receipt_no`);
      const result = await response.json();

      if (result.status === "success") {
        return result.receipt_no;
      } else {
        throw new Error(result.message || "Failed to generate receipt number");
      }
    } catch (err) {
      console.error("Generate receipt number error:", err);
      // Fallback to client-side generation if API fails
      const year = new Date().getFullYear().toString().slice(-2);
      const lastRecord = fees
        .filter(f => f.receipt_no.startsWith(`AUXITI${year}`))
        .sort((a, b) => b.id - a.id)[0];

      let nextNum = 1;
      if (lastRecord) {
        const lastNum = parseInt(lastRecord.receipt_no.slice(-3), 10);
        nextNum = lastNum + 1;
      }

      return `AUXITI${year}${String(nextNum).padStart(3, '0')}`;
    }
  };

  const handleTradeChange = (e) => {
    const trade = e.target.value;
    setSelectedTrade(trade);
    setSelectedBatch("");
    setSelectedId("");
    setStudent(null);

    if (trade) {
      const tradeStudents = students.filter((s) => s.trade === trade);
      const uniqueBatches = [
        ...new Set(
          tradeStudents.map((s) =>
            new Date(s.date_of_admission).getFullYear().toString()
          )
        ),
      ];
      setBatches(uniqueBatches);
    } else {
      setBatches([]);
      setFilteredStudents([]);
    }
  };

  const handleBatchChange = (e) => {
    const batch = e.target.value;
    setSelectedBatch(batch);
    setSelectedId("");
    setStudent(null);

    if (batch) {
      const filtered = students.filter(
        (s) =>
          s.trade === selectedTrade &&
          new Date(s.date_of_admission).getFullYear().toString() === batch
      );
      setFilteredStudents(filtered);
    } else {
      setFilteredStudents([]);
    }
  };

  const handleSelectStudent = (id) => {
    setSelectedId(id);
    if (!id) return setStudent(null);
    const selected = students.find((s) => s.student_id.toString() === id);
    setStudent(selected || null);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

const handleSave = async () => {
  if (!student || !formData.feeType || !formData.amount || !receiptNo) {
    alert("Please fill all required fields.");
    return;
  }

  const payload = {
    receipt_no: receiptNo,
    student_name: student.name,
    trade: student.trade,
    batch: selectedBatch,
    fee_type: formData.feeType,
    amount: formData.amount,
    payment_mode: formData.paymentMode,
    purpose: formData.purpose,
    date: formData.date,
  };

  const method = editFee ? "PUT" : "POST";
  const url = editFee ? `${API_URL}?id=${editFee.id}` : API_URL;

  try {
    console.log("Sending:", method, url, payload); // Debug

    const response = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const text = await response.text();
    console.log("Raw Response:", text || "[EMPTY]"); // Debug

    if (!text.trim()) {
      throw new Error("Server returned empty response");
    }

    let result;
    try {
      result = JSON.parse(text);
    } catch (e) {
      throw new Error("Invalid JSON from server: " + text.substring(0, 100));
    }

    if (result.status === "success") {
      alert(editFee ? "Updated successfully!" : "Saved successfully!");
      await fetchFees();
      resetForm();
    } else {
      throw new Error(result.message || "Operation failed");
    }
  } catch (err) {
    console.error("Save Error:", err);
    alert("Error: " + err.message);
  }
};

  const resetForm = () => {
    setSelectedTrade("");
    setSelectedBatch("");
    setSelectedId("");
    setStudent(null);
    setReceiptNo("");
    setFormData({
      feeType: "",
      amount: "",
      paymentMode: "",
      purpose: "",
      date: new Date().toISOString().split("T")[0],
    });
    setEditFee(null);
    setShowForm(false);
  };

  // ------------------------------
  // EXPORT: EXCEL – ONLY FILTERED DATA
  // ------------------------------
  const exportToExcel = () => {
    // Use filteredFees (already includes search + all filters)
    const dataToExport = filteredFees;

    if (!dataToExport || dataToExport.length === 0) {
      alert("No fee data to export (filtered results are empty)");
      return;
    }

    // Map filtered data with proper formatting
    const exportData = dataToExport.map(fee => ({
      'Receipt No': fee.receipt_no || "N/A",
      'Student Name': fee.student_name || "",
      'Trade': fee.trade || "",
      'Batch': fee.batch || "",
      'Fee Type': fee.fee_type || "",
      'Amount (₹)': fee.amount || "",
      'Payment Mode': fee.payment_mode || "",
      'Purpose': fee.purpose || "",
      'Date': fee.date || "",
      'Created At': fee.created_at || "",
      'Updated At': fee.updated_at || ""
    }));

    // Convert JSON → Excel worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData);

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Fee Records");

    // Column widths
    const wscols = [
      { wch: 18 }, // Receipt No
      { wch: 25 }, // Student Name
      { wch: 20 }, // Trade
      { wch: 10 }, // Batch
      { wch: 15 }, // Fee Type
      { wch: 12 }, // Amount
      { wch: 15 }, // Payment Mode
      { wch: 25 }, // Purpose
      { wch: 12 }, // Date
      { wch: 20 }, // Created At
      { wch: 20 }  // Updated At
    ];
    worksheet['!cols'] = wscols;

    // Smart filename: add "_Filtered" if any filter/search is active
    const isFiltered = activeFilterCount() > 0 || searchQuery.trim();
    const filterTag = isFiltered ? "_Filtered" : "";
    const today = new Date().toISOString().split('T')[0];
    const filename = `Fee_Records${filterTag}_${today}.xlsx`;

    // Export
    XLSX.writeFile(workbook, filename);
  };

  // const handlePrint = () => {
  //   if (!printRef.current) return;

  //   const printContents = printRef.current.innerHTML;

  //   const popup = window.open("", "_blank", "width=900,height=1000,scrollbars=yes,resizable=yes");

  //   popup.document.write(`
  //     <html>
  //       <head>
  //         <title>Fee Receipt</title>
  //         <style>
  //           @page {
  //             size: A4;
  //             margin: 0;
  //           }

  //           body {
  //             margin: 0;
  //             padding: 0;
  //             font-family: 'Poppins', sans-serif;
  //             position: relative;
  //             min-height: 297mm; /* A4 height */
  //             overflow: hidden;
  //           }

  //           /* Force-print letterhead using ::before (works even if background printing is disabled) */
  //           body::before {
  //             content: '';
  //             position: fixed;
  //             top: 0;
  //             left: 0;
  //             width: 210mm; /* A4 width */
  //             height: 297mm; /* A4 height */
  //             background-image: url('${LETTERHEAD_IMAGE}');
  //             background-repeat: no-repeat;
  //             background-position: top center;
  //             background-size: 100% auto;
  //             z-index: -1;
  //             pointer-events: none;
  //             display: block !important;
  //             -webkit-print-color-adjust: exact !important;
  //             color-adjust: exact !important;
  //           }

  //           .content {
  //             padding: 80mm 20mm 20mm 20mm;
  //             position: relative;
  //             z-index: 1;
  //             font-size: 1.1rem;
  //             line-height: 1.7;
  //           }

  //           .title {
  //             text-align: center;
  //             text-decoration: underline;
  //             font-size: 1.5rem;
  //             margin-bottom: 50px;
  //           }

  //           .receipt-no {
  //             text-align: right;
  //             font-size: 1.3rem;
  //             font-weight: bold;
  //             margin-bottom: 50px;
  //           }

  //           table {
  //             width: 100%;
  //             margin-top: 25px;
  //             border-collapse: collapse !important;
  //             margin-bottom: 50px;
  //           }

  //           td {
  //             padding: 8px 5px;
  //             border: 1px solid black !important;
  //           }

  //           .signature {
  //             margin-top: 160px;
  //             text-align: right;
  //             font-weight: bold;
  //           }

  //           /* Ensure borders and background print correctly */
  //           @media print {
  //             body::before {
  //               -webkit-print-color-adjust: exact !important;
  //               color-adjust: exact !important;
  //             }
  //             table, td, th {
  //               border: 1px solid black !important;
  //               -webkit-print-color-adjust: exact !important;
  //               color-adjust: exact !important;
  //             }
  //           }
  //         </style>
  //       </head>
  //       <body>
  //         <div class="content">
  //           <div class="title">Fee Receipt</div>
  //           <div class="receipt-no">Receipt No: <span id="receipt-no"></span></div>

  //           ${printContents}

  //           <div class="signature">Principal Signature</div>
  //         </div>

  //         <script>
  //           document.addEventListener('DOMContentLoaded', () => {
  //             // Safely extract receipt number if present in the HTML
  //             const content = ${JSON.stringify(printContents)};
  //             const match = content.match(/Receipt No:\\s*([^<]+)/i);
  //             if (match) {
  //               document.getElementById('receipt-no').textContent = match[1].trim();
  //             }

  //             // Trigger print
  //             window.print();
  //           });

  //           // Close after print
  //           window.onafterprint = () => window.close();

  //           // Chrome/Edge fallback when print dialog is closed manually
  //           const mediaQuery = window.matchMedia('print');
  //           const handleChange = (mql) => {
  //             if (!mql.matches) {
  //               setTimeout(() => window.close(), 100);
  //             }
  //           };
  //           mediaQuery.addEventListener('change', handleChange);
  //         </script>
  //       </body>
  //     </html>
  //   `);

  //   popup.document.close();
  //   popup.focus();
  // };
  const handlePrintForRecord = (fee) => {
    const formattedDate = new Date(fee.date).toLocaleDateString("en-GB");

    const popup = window.open("", "_blank", "width=900,height=1000,scrollbars=yes,resizable=yes");

    popup.document.write(`
    <html>
      <head>
        <title>Fee Receipt - ${fee.receipt_no}</title>
        <style>
          @page { size: A4; margin: 0; }
          
          /* Container to allow absolute positioning */
          body {
            margin: 0;
            padding: 0;
            font-family: 'Poppins', sans-serif;
            position: relative;
            min-height: 297mm; /* A4 height */
            overflow: hidden;
          }

          /* Force letterhead using ::before (prints even if background disabled) */
          body::before {
            content: '';
            position: fixed;
            top: 0;
            left: 0;
            width: 210mm; /* A4 width */
            height: 297mm; /* A4 height */
            background-image: url('${LETTERHEAD_IMAGE}');
            background-repeat: no-repeat;
            background-position: top center;
            background-size: 100% auto;
            z-index: -1;
            pointer-events: none;
            display: block !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          /* Content padding to avoid overlapping letterhead */
          .content {
            padding: 80mm 20mm 20mm 20mm;
            position: relative;
            z-index: 1;
            font-size: 1.1rem;
            line-height: 1.7;
          }

          .title {
            text-align: center;
            text-decoration: underline;
            font-size: 1.5rem;
            margin-bottom: 50px;
          }

          .receipt-no {
            text-align: right;
            font-size: 1.3rem;
            font-weight: bold;
            margin-bottom: 50px;
          }

          table {
            width: 100%;
            margin-top: 25px;
            border-collapse: collapse !important;
            margin-bottom: 50px;
          }

          td {
            padding: 8px 5px;
            border: 1px solid black !important;
          }

          .signature {
            margin-top: 160px;
            text-align: right;
            font-weight: bold;
          }

          /* Force borders and colors in print */
          @media print {
            body::before {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            table, td {
              border: 1px solid black !important;
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
          }
        </style>
      </head>
      <body>
        <div class="content">
          <div class="title">Fee Receipt</div>
          <div class="receipt-no">Receipt No: ${fee.receipt_no}</div>

          <table>
            <tr><td><strong>Name</strong></td><td>${fee.student_name}</td></tr>
            <tr><td><strong>Trade</strong></td><td>${fee.trade}</td></tr>
            <tr><td><strong>Batch</strong></td><td>${fee.batch}</td></tr>
            <tr><td><strong>Date</strong></td><td>${formattedDate}</td></tr>
            <tr><td><strong>Fee Type</strong></td><td>${fee.fee_type}</td></tr>
            <tr><td><strong>Amount</strong></td><td>₹${fee.amount}</td></tr>
            <tr><td><strong>Payment Mode</strong></td><td>${fee.payment_mode}</td></tr>
            ${fee.purpose ? `<tr><td><strong>Purpose</strong></td><td>${fee.purpose}</td></tr>` : ""}
          </table>

          <div class="signature">Principal Signature</div>
        </div>

        <script>
          window.onload = () => window.print();
          window.onafterprint = () => window.close();

          // Fallback for Chrome/Edge when print is canceled
          const mediaQuery = window.matchMedia('print');
          const handleChange = (mql) => {
            if (!mql.matches) {
              setTimeout(() => window.close(), 100);
            }
          };
          mediaQuery.addEventListener('change', handleChange);
        </script>
      </body>
    </html>
  `);

    popup.document.close();
    popup.focus();
  };

  const handleView = (fee) => {
    setViewFee(fee);
    setShowViewModal(true);
  };

  const handleEdit = async (fee) => {
  setEditFee(fee);
  setReceiptNo(fee.receipt_no);
  setSelectedTrade(fee.trade);
  setSelectedBatch(fee.batch);

  setFormData({
    feeType: fee.fee_type,
    amount: fee.amount,
    paymentMode: fee.payment_mode,
    purpose: fee.purpose || "",
    date: fee.date,
  });

  // --- FIND STUDENT BY NAME (with trim & case-insensitive) ---
  const matchedStudent = students.find(s =>
    s.name?.trim().toLowerCase() === fee.student_name?.trim().toLowerCase()
  );

  if (matchedStudent) {
    setSelectedId(matchedStudent.student_id);
    setStudent(matchedStudent);
  } else {
    // Fallback: create minimal student object
    setStudent({
      student_id: "",
      name: fee.student_name,
      trade: fee.trade,
    });
    setSelectedId("");
  }

  setShowForm(true);
};

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this fee record permanently?")) return;
    try {
      const response = await fetch(`${API_URL}?id=${id}`, {
        method: "DELETE"
      });

      const result = await response.json();

      if (result.status === "success") {
        alert("Fee record deleted!");
        fetchFees();
      } else {
        throw new Error(result.message || "Delete failed");
      }
    } catch (err) {
      console.error("Delete error:", err);
      alert("Error: " + err.message);
    }
  };

  const handleNewReceipt = async () => {
    resetForm();
    const newReceiptNo = await generateReceiptNo();
    setReceiptNo(newReceiptNo);
    setShowForm(true);
  };

  const toggleTemp = (value, arr, setArr) => {
    if (arr.includes(value)) setArr(arr.filter((a) => a !== value));
    else setArr([...arr, value]);
  };

  const openFilterModal = () => {
    // Initialize temp arrays with current filters
    setTempTrades([...filterTrades]);
    setTempBatches([...filterBatches]);
    setTempFeeTypes([...filterFeeTypes]);
    setShowFilterModal(true);
  };

  const applyFiltersFromModal = () => {
    setFilterTrades([...tempTrades]);
    setFilterBatches([...tempBatches]);
    setFilterFeeTypes([...tempFeeTypes]);
    setShowFilterModal(false);
  };

  const clearModalSelections = () => {
    setTempTrades([]);
    setTempBatches([]);
    setTempFeeTypes([]);
  };

  const clearAllFilters = () => {
    setFilterTrades([]);
    setFilterBatches([]);
    setFilterFeeTypes([]);
    setSearchQuery("");
  };

  const activeFilterCount = () =>
    filterTrades.length +
    filterBatches.length +
    filterFeeTypes.length;

  // Filter the fees based on applied filters
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredFees = fees.filter(fee => {
    // Filter by trades
    if (filterTrades.length > 0 && !filterTrades.includes(fee.trade)) return false;

    // Filter by batches
    if (filterBatches.length > 0 && !filterBatches.includes(fee.batch)) return false;

    // Filter by fee types
    if (filterFeeTypes.length > 0 && !filterFeeTypes.includes(fee.fee_type)) return false;

    // Search by query
    if (normalizedQuery) {
      const fields = [
        fee.receipt_no || "",
        fee.student_name || "",
        fee.trade || "",
        fee.batch || "",
        fee.fee_type || "",
        fee.payment_mode || "",
        fee.purpose || "",
        fee.date || ""
      ];
      return fields.some(field => field.toLowerCase().includes(normalizedQuery));
    }

    return true;
  });

  // Get unique values for filters
  const uniqueFeeTypes = Array.from(new Set(fees.map(f => f.fee_type).filter(Boolean)));
  const uniqueBatches = Array.from(new Set(fees.map(f => f.batch).filter(Boolean)));

  const formattedDate = formData.date ? new Date(formData.date).toLocaleDateString("en-GB") : "";

  if (loading) {
    return (
      <div className="d-flex dashboard-container">
        <Sidebar />
        <div className="main-content flex-grow-1">
          <Header />
          <div className="container py-5 text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
          <DashboardFooter />
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex dashboard-container">
      <Sidebar />
      <div className="main-content flex-grow-1">
        <Header />

        <div className="container py-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h3 className="fw-bold text-primary">Fee Records</h3>
            <div className="d-flex align-items-center">
              <button
                className="btn btn-outline-secondary me-2"
                onClick={fetchAll}
                title="Refresh data"
              >
                <FaSync className="me-1" /> Refresh
              </button>
              <button
                className="btn btn-success me-2"
                onClick={exportToExcel}
                title="Export filtered records to Excel"
              >
                <FaDownload className="me-2" />
                Export Excel
                {filteredFees.length !== fees.length && (
                  <span className="badge bg-light text-dark ms-1">
                    {filteredFees.length}
                  </span>
                )}
              </button>
              <button
                className="btn btn-outline-primary me-2 d-flex align-items-center"
                onClick={openFilterModal}
                title="Filter records"
              >
                <FaFilter className="me-2" /> Filters
                {activeFilterCount() > 0 && (
                  <span className="badge bg-danger ms-2">{activeFilterCount()}</span>
                )}
              </button>
              <button
                className="btn btn-primary d-flex align-items-center"
                onClick={handleNewReceipt}
                title="Create new fee receipt"
              >
                <FaPlus className="me-2" /> New Fee Receipt
              </button>
            </div>
          </div>

          {/* Search bar */}
          <div className="card mb-4 p-3">
            <div className="row g-2 align-items-center">
              <div className="col-md-8">
                <div className="input-group">
                  <span className="input-group-text"><FaSearch /></span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by receipt no, name, trade, batch, fee type..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => setSearchQuery("")}
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>
              </div>
              <div className="col-md-4 text-end">
                <button
                  className="btn btn-outline-secondary"
                  onClick={clearAllFilters}
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          </div>

          {/* FORM */}
          {showForm && (
            <div className="card p-4 shadow-sm mb-4">
              <h5 className="fw-bold mb-3 text-primary">
                {editFee ? "Edit Fee Receipt" : "New Fee Receipt"}
              </h5>
              {/* Trade/Batch/Student */}
              <div className="row g-3">
                <div className="col-md-4 mb-3">
                  <label className="form-label fw-semibold">Receipt No *</label>
                  <input
                    type="text"
                    className="form-control form-control-lg fw-bold"
                    value={receiptNo}
                    onChange={(e) => setReceiptNo(e.target.value)}
                    disabled={!!editFee}
                    required
                    readOnly={!editFee}
                    style={{ fontSize: '1.1rem' }}
                  />
                  {!receiptNo && !editFee && (
                    <button
                      className="btn btn-sm btn-outline-secondary mt-2"
                      onClick={async () => {
                        const newReceiptNo = await generateReceiptNo();
                        setReceiptNo(newReceiptNo);
                      }}
                    >
                      Generate Receipt No
                    </button>
                  )}
                  <small className="text-muted">Format: AUXITI{new Date().getFullYear().toString().slice(-2)}001</small>
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Select Trade</label>
                  <select
                    className="form-select"
                    value={selectedTrade}
                    onChange={handleTradeChange}
                    required
                  >
                    <option value="">-- Select Trade --</option>
                    {trades.map((trade) => (
                      <option key={trade} value={trade}>
                        {trade}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-semibold">Select Batch</label>
                  <select
                    className="form-select"
                    value={selectedBatch}
                    onChange={handleBatchChange}
                    disabled={!selectedTrade}
                    required
                  >
                    <option value="">-- Select Batch --</option>
                    {batches.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-semibold">Select Student</label>
                  <select
                    className="form-select"
                    value={selectedId}
                    onChange={(e) => handleSelectStudent(e.target.value)}
                    disabled={!selectedBatch}
                    required
                  >
                    <option value="">-- Select Student --</option>
                    {filteredStudents.map((s) => (
                      <option key={s.student_id} value={s.student_id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {student && (
                <>
                  <div className="row g-3 mt-4">
                    <div className="col-md-3">
                      <label className="form-label fw-semibold">Fee Type *</label>
                      <select
                        className="form-select"
                        name="feeType"
                        value={formData.feeType}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select</option>
                        <option>College Fee</option>
                        <option>Exam Fee</option>
                        <option>Other Fee</option>
                      </select>
                    </div>

                    <div className="col-md-3">
                      <label className="form-label fw-semibold">Amount (₹) *</label>
                      <input
                        type="number"
                        className="form-control"
                        name="amount"
                        value={formData.amount}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="col-md-3">
                      <label className="form-label fw-semibold">Payment Mode *</label>
                      <select
                        className="form-select"
                        name="paymentMode"
                        value={formData.paymentMode}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select</option>
                        <option>Cash</option>
                        <option>GPay</option>
                        <option>Card</option>
                      </select>
                    </div>

                    <div className="col-md-3">
                      <label className="form-label fw-semibold">Date *</label>
                      <input
                        type="date"
                        className="form-control"
                        name="date"
                        value={formData.date}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    {formData.feeType === "Other Fee" && (
                      <div className="col-md-12 mt-3">
                        <label className="form-label fw-semibold">Purpose</label>
                        <input
                          type="text"
                          className="form-control"
                          name="purpose"
                          value={formData.purpose}
                          onChange={handleChange}
                        />
                      </div>
                    )}
                  </div>

                  <div className="text-center mt-4">
                    {/* <button className="btn btn-primary px-4 me-3" onClick={handlePrint}>
                      Print Receipt
                    </button> */}

                    {/* <button
                      className="btn btn-warning px-4 me-3"
                      onClick={handleDownloadPDF}
                    >
                      Download PDF
                    </button> */}

                    <button className="btn btn-success px-4 me-3" onClick={handleSave}>
                      {editFee ? "Update Receipt" : "Save Receipt"}
                    </button>

                    <button className="btn btn-secondary px-4" onClick={resetForm}>
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* PRINT PDF AREA */}
          {student && receiptNo && (
            <div
              ref={printRef}
              style={{
                display: "none",
                width: "210mm",
                minHeight: "297mm",
                backgroundImage: `url(${LETTERHEAD_IMAGE})`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "top center",
                backgroundSize: "100%",
                padding: "80mm 20mm 20mm 20mm",
                fontFamily: "Poppins, sans-serif",
              }}
            >
              <h2 className="text-center" style={{ textDecoration: "underline" }}>
                Fee Receipt
              </h2>

              <div className="receipt-no text-center mb-4">
                Receipt No: {receiptNo}
              </div>

              <p><strong>Name:</strong> {student.name}</p>
              <p><strong>Trade:</strong> {student.trade}</p>
              <p><strong>Batch:</strong> {selectedBatch}</p>
              <p><strong>Date:</strong> {formattedDate}</p>

              <p className="mt-4"><strong>Fee Type:</strong> {formData.feeType}</p>
              <p><strong>Amount:</strong> ₹{formData.amount}</p>
              <p><strong>Payment Mode:</strong> {formData.paymentMode}</p>

              {formData.purpose && (
                <p><strong>Purpose:</strong> {formData.purpose}</p>
              )}

              <div className="sign" style={{ marginTop: "50px", textAlign: "right", fontWeight: "bold" }}>
                Principal Signature
              </div>
            </div>
          )}

          {/* STORED RECORDS */}
          <div className="card mt-2 p-4 shadow-sm">
            <div className="table-responsive">
              <table className="table table-striped table-bordered align-middle">
                <thead className="table-dark text-center">
                  <tr>
                    <th>Receipt No</th>
                    <th>Student Name</th>
                    <th>Trade</th>
                    <th>Batch</th>
                    <th>Fee Type</th>
                    <th>Amount (₹)</th>
                    <th>Date</th>
                    <th>Payment Mode</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFees.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="text-center text-muted">
                        No fee records found
                      </td>
                    </tr>
                  ) : (
                    filteredFees.map((fee) => (
                      <tr key={fee.id}>
                        <td className="fw-bold">{fee.receipt_no}</td>
                        <td>{fee.student_name}</td>
                        <td>{fee.trade}</td>
                        <td>{fee.batch}</td>
                        <td>{fee.fee_type}</td>
                        <td className="text-end">₹{fee.amount}</td>
                        <td>{fee.date}</td>
                        <td>{fee.payment_mode}</td>
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-info me-1"
                            onClick={() => handleView(fee)}
                            title="View details"
                          >
                            <FaEye />
                          </button>
                          <button
                            className="btn btn-sm btn-warning me-1"
                            onClick={() => handleEdit(fee)}
                            title="Edit receipt"
                          >
                            <FaEdit />
                          </button>
                          {/* <button 
                            className="btn btn-sm btn-danger" 
                            onClick={() => handleDelete(fee.id)}
                            title="Delete receipt"
                          >
                            <FaTrash />
                          </button> */}
                          <button
                            className="btn btn-sm btn-primary ms-1"
                            onClick={() => handlePrintForRecord(fee)}
                            title="Print this receipt"
                          >
                            <FaDownload />
                          </button>

                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* View Modal */}
        {showViewModal && viewFee && (
          <div
            className="modal show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
            onClick={() => setShowViewModal(false)}
          >
            <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Fee Receipt Details</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowViewModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6">
                      <p><strong>Receipt No:</strong> {viewFee.receipt_no}</p>
                      <p><strong>Student Name:</strong> {viewFee.student_name}</p>
                      <p><strong>Trade:</strong> {viewFee.trade}</p>
                      <p><strong>Batch:</strong> {viewFee.batch}</p>
                      <p><strong>Fee Type:</strong> {viewFee.fee_type}</p>
                      <p><strong>Amount:</strong> ₹{viewFee.amount}</p>
                    </div>
                    <div className="col-md-6">
                      <p><strong>Payment Mode:</strong> {viewFee.payment_mode}</p>
                      <p><strong>Purpose:</strong> {viewFee.purpose || "—"}</p>
                      <p><strong>Date:</strong> {viewFee.date}</p>
                      <p><strong>Created At:</strong> {viewFee.created_at}</p>
                      <p><strong>Updated At:</strong> {viewFee.updated_at || "—"}</p>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowViewModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filter Modal */}
        {showFilterModal && (
          <div
            className="modal show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
            onClick={() => setShowFilterModal(false)}
          >
            <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Filters</h5>
                  <button type="button" className="btn-close" onClick={() => setShowFilterModal(false)}></button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    {/* Trades */}
                    <div className="col-md-4 mb-3">
                      <h6>Trades</h6>
                      <div className="filter-list" style={{ maxHeight: 160, overflowY: "auto", border: "1px solid #eee", padding: 8, borderRadius: 4 }}>
                        {trades.length === 0 ? (
                          <div className="text-muted">No trades available</div>
                        ) : (
                          trades.map((t) => (
                            <div key={t} className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`trade_${t}`}
                                checked={tempTrades.includes(t)}
                                onChange={() => toggleTemp(t, tempTrades, setTempTrades)}
                              />
                              <label className="form-check-label" htmlFor={`trade_${t}`}>{t}</label>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Batches */}
                    <div className="col-md-4 mb-3">
                      <h6>Batches</h6>
                      <div className="filter-list" style={{ maxHeight: 160, overflowY: "auto", border: "1px solid #eee", padding: 8, borderRadius: 4 }}>
                        {uniqueBatches.length === 0 ? (
                          <div className="text-muted">No batches available</div>
                        ) : (
                          uniqueBatches.map((b) => (
                            <div key={b} className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`batch_${b}`}
                                checked={tempBatches.includes(b)}
                                onChange={() => toggleTemp(b, tempBatches, setTempBatches)}
                              />
                              <label className="form-check-label" htmlFor={`batch_${b}`}>{b}</label>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Fee Types */}
                    <div className="col-md-4 mb-3">
                      <h6>Fee Types</h6>
                      <div className="filter-list" style={{ maxHeight: 160, overflowY: "auto", border: "1px solid #eee", padding: 8, borderRadius: 4 }}>
                        {uniqueFeeTypes.length === 0 ? (
                          <div className="text-muted">No fee types available</div>
                        ) : (
                          uniqueFeeTypes.map((ft) => (
                            <div key={ft} className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`fee_${ft}`}
                                checked={tempFeeTypes.includes(ft)}
                                onChange={() => toggleTemp(ft, tempFeeTypes, setTempFeeTypes)}
                              />
                              <label className="form-check-label" htmlFor={`fee_${ft}`}>{ft}</label>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <div className="w-100 d-flex justify-content-between">
                    <div>
                      <button
                        className="btn btn-outline-secondary me-2"
                        onClick={() => clearModalSelections()}
                        type="button"
                      >
                        Clear Selections
                      </button>
                      <button
                        className="btn btn-outline-danger"
                        onClick={() => {
                          clearModalSelections();
                          clearAllFilters();
                          setShowFilterModal(false);
                        }}
                        type="button"
                      >
                        Clear All Filters
                      </button>
                    </div>
                    <div>
                      <button
                        className="btn btn-secondary me-2"
                        onClick={() => setShowFilterModal(false)}
                        type="button"
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={() => applyFiltersFromModal()}
                        type="button"
                      >
                        Apply Filters
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <DashboardFooter />
      </div>
    </div>
  );
};

export default FeeDetails;